// web-staking-estimator-frontend/src/hooks/map/useMapView.js
import { useRef, useEffect } from "react";
import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Extent from "@arcgis/core/geometry/Extent";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import { attachOverlays } from "../../map/overlay";
import { loadUiCfg } from "../../services/map/configService";

/**
 * Hook to initialize and manage the ArcGIS MapView
 * Returns refs to the view and graphics layers
 */
export function useMapView({ mapDiv, onBasemapReady }) {
  const viewRef = useRef(null);
  const clickLayerRef = useRef(null);
  const resultsLayerRef = useRef(null);

  useEffect(() => {
    if (!mapDiv.current) return;

    const map = new Map({ basemap: "streets-vector" });
    const view = new MapView({
      container: mapDiv.current,
      map,
      highlightOptions: { color: [0, 120, 255] },
    });
    viewRef.current = view;

    // Attach all configured overlays (infrastructure, service boundary, etc.)
    attachOverlays(view);

    // Create graphics layers
    const clickLayer = new GraphicsLayer({ listMode: "hide" });
    clickLayerRef.current = clickLayer;
    const resultsLayer = new GraphicsLayer({ listMode: "show" });
    resultsLayerRef.current = resultsLayer;
    map.addMany([resultsLayer, clickLayer]);

    // Set default extent from config (with hardcoded fallback)
    view.when(async () => {
      try {
        const uiCfg = await loadUiCfg();
        const configExtent = uiCfg?.map?.defaultExtent;
        
        let geoExtent;
        if (configExtent && configExtent.xmin && configExtent.ymin && configExtent.xmax && configExtent.ymax) {
          // Use config-driven extent
          geoExtent = new Extent({
            xmin: configExtent.xmin,
            ymin: configExtent.ymin,
            xmax: configExtent.xmax,
            ymax: configExtent.ymax,
            spatialReference: { wkid: configExtent.wkid || 4326 },
          });
        } else {
          // Fallback to hardcoded Oregon area
          geoExtent = new Extent({
            xmin: -119.64094590215939,
            ymin: 43.45505949954564,
            xmax: -117.35241574718627,
            ymax: 45.76247392572059,
            spatialReference: { wkid: 4326 },
          });
        }
        
        const wmExtent = webMercatorUtils.geographicToWebMercator(geoExtent);
        await view.goTo(wmExtent);
      } catch (e) {
        console.warn("goTo extent failed:", e);
      }
    });

    // Apply default basemap from config
    (async () => {
      try {
        const uiCfg = await loadUiCfg();
        const debug = !!uiCfg?.pdf?.debug;
        if (debug) console.log("[cfg]/api/config =>", uiCfg);
        const base = uiCfg?.map?.basemapDefault;
        if (base === "hybrid") {
          if (debug) console.log("[cfg] setting default basemap to hybrid");
          try {
            view.map.basemap = "hybrid";
            onBasemapReady?.(true);
          } catch (e) {
            console.warn("basemap switch failed:", e);
          }
        }
      } catch (e) {
        console.warn("[cfg] loadUiCfg failed:", e);
      }
    })();

    return () => {
      view.destroy();
      viewRef.current = null;
      clickLayerRef.current = null;
      resultsLayerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    viewRef,
    clickLayerRef,
    resultsLayerRef,
  };
}
